import React, { useEffect, useState, FC } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { JobListing } from '../../types/components';
// import jobService from '../../services/job.service'; // Not currently used
import { useAuth } from '../../hooks/useAuth';
import { CopmanyUser } from '../../types/api';
import { useCompany } from '../../context/CompanyContext';
import { mapLocations } from '../../utils/jobPostMapper';

const JobList: FC = () => {
  console.log('JobList component is rendering');
  const { user } = useAuth();
  const { jobPosts, deleteJobPost , loadingState} = useCompany();
  const [jobs, setJobs] = useState<JobListing[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    jobId: number | null;
    jobTitle: string;
  }>({
    isOpen: false,
    jobId: null,
    jobTitle: ''
  });

  useEffect(() => {
    const fetchJobs = async () => {
      if (!(user && user.role === 'Company' && (user as CopmanyUser).companyId)) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const apiJobs = (jobPosts || []).map((p) => {
          const status: JobListing['status'] = p.postStatus || 'Draft'; // Use postStatus directly
          return {
            id: p.postId,
            title: p.jobTitle || 'Untitled Post',
            department: '-',  // TODO check if required
            location: mapLocations(p.locationIds),
            jobType: (p.jobType),
            experienceLevel: p.minExperience != undefined ? p.minExperience == 0 ? 'Fresher' : `${p.minExperience.toString()}+ years` : '-',
            salaryMin: p.minSalary || 0,
            salaryMax: p.maxSalary || 0,
            currency: p.salaryCurrency || 'INR', // Use Currency enum directly
            status,
            applicants: 0, // TODO connect to api
            postedDate: p.createdOn || '',
            deadline: p.expirationDt || '',
            isUrgent: false,
            isRemote: p.workMode === 'Remote'
          } as JobListing;
        });
        setJobs(apiJobs);
      } catch (e: any) {
        setError(e?.message || 'Failed to load jobs');
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [user, jobPosts]);

  const getStatusColor = (status: JobListing['status']): string => {
    switch (status) {
      case 'Draft': return 'bg-yellow-100 text-yellow-800';
      case 'UnderReview': return 'bg-gray-100 text-gray-800';
      case 'Scheduled': return 'bg-green-100 text-green-800';
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Complete': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDeleteJob = (jobId: number) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      setDeleteDialog({
        isOpen: true,
        jobId,
        jobTitle: job.title
      });
    }
  };

  const confirmDeleteJob = async () => {
    if (deleteDialog.jobId) {
      try {
        await deleteJobPost(deleteDialog.jobId);
        setDeleteDialog({
          isOpen: false,
          jobId: null,
          jobTitle: ''
        });
      } catch (error) {
        setError("Failed to delete job post.");
      }
    }
  };

  const cancelDeleteJob = () => {
    setDeleteDialog({
      isOpen: false,
      jobId: null,
      jobTitle: ''
    });
  };

  const handlePublishDraft = (jobId: number) => {
    setJobs(prevJobs => 
      prevJobs.map(job => 
        job.id === jobId ? { ...job, status: 'Active' as const } : job
      )
    );
  };

  const formatSalary = (min: number, max: number, currency: string): string => {
    const formatNumber = (num: number): string => {
      if (num >= 100000) {
        return `${(num / 100000).toFixed(1)}L`;
      }
      return num.toLocaleString();
    };
    return `${currency} ${formatNumber(min)} - ${formatNumber(max)}`;
  };

  const filteredJobs = statusFilter === 'all' 
    ? jobs 
    : jobs.filter(job => job.status === statusFilter);

  return (
    <div className="space-y-6 mt-6 md:mt-8">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">{error}</div>
      )}
      
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Postings</h1>
          <p className="text-gray-600">Manage your job postings and track applications</p>
        </div>
        <Link to="/company/jobs/create">
          <Button>Post New Job</Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Total Jobs</div>
          <div className="text-2xl font-bold text-gray-900">{loading ? '—' : jobs.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Active Jobs</div>
          <div className="text-2xl font-bold text-green-600">
            {loading ? '—' : jobs.filter(job => job.status === 'Active').length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Draft Jobs</div>
          <div className="text-2xl font-bold text-gray-600">
            {loading ? '—' : jobs.filter(job => job.status === 'Draft').length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Total Applicants</div>
          <div className="text-2xl font-bold text-primary-lightGreen">
            {loading ? '—' : jobs.reduce((sum, job) => sum + job.applicants, 0)}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Urgent Jobs</div>
          <div className="text-2xl font-bold text-red-600">
            {loading ? '—' : jobs.filter(job => job.isUrgent).length}
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Filter Jobs</h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-green-100 text-green-800 border border-green-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Jobs ({loading ? 0 : jobs.length})
            </button>
            <button
              onClick={() => setStatusFilter('Active')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'Active'
                  ? 'bg-green-100 text-green-800 border border-green-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active ({loading ? 0 : jobs.filter(job => job.status === 'Active').length})
            </button>
            <button
              onClick={() => setStatusFilter('Draft')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'Draft'
                  ? 'bg-gray-100 text-gray-800 border border-gray-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Drafts ({loading ? 0 : jobs.filter(job => job.status === 'Draft').length})
            </button>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {statusFilter === 'all' ? 'All Job Postings' : `${statusFilter} Jobs`} ({filteredJobs.length})
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Job Title
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Salary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Applicants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(loading ? [] : filteredJobs).map((job) => (
                <tr key={job.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          {job.title}
                          {job.isUrgent && (
                            <Badge variant="danger" size="sm">Urgent</Badge>
                          )}
                          {job.isRemote && (
                            <Badge variant="info" size="sm">Remote</Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {job.jobType} • {job.experienceLevel}
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {job.department}
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {job.location.slice(0,4).map(city => `${city.cityName},`)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatSalary(job.salaryMin, job.salaryMax, job.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <Link 
                      to={`/company/applications/${job.id}`}
                      className="text-primary-lightGreen hover:text-primary-black font-medium"
                    >
                      {job.applicants} applicants
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                      {job.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <Link to={`/company/jobs/${job.id}/edit`}>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-green-600 hover:bg-green-50"
                          iconOnly
                          aria-label="Edit"
                          title="Edit"
                          icon={
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.25 2.25 0 113.182 3.182L7.125 19.588a4.5 4.5 0 01-1.591 1.01l-3.02 1.007 1.007-3.02a4.5 4.5 0 011.01-1.591L16.862 3.487z" />
                            </svg>
                          }
                        />
                      </Link>
                      
                      {job.status === 'Draft' ? (
                        <>
                          {/* Publish should be done from JobPost */}
                          {/* <Button 
                            size="sm" 
                            onClick={() => handlePublishDraft(job.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            iconOnly
                            aria-label="Publish"
                            title="Publish"
                            icon={
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12 18 18.75 12 18.75 2.25 12 2.25 12z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" />
                              </svg>
                            }
                          /> */}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleDeleteJob(job.id)}
                            className="text-red-600 hover:bg-red-50"
                            iconOnly
                            aria-label="Delete"
                            title="Delete"
                            icon={
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.108 1.022.17m-1.022-.17L18.16 19.673A2.25 2.25 0 0115.917 21.75H8.083a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .567c.34-.062.68-.118 1.022-.17m0 0A48.11 48.11 0 0112 4.5c1.886 0 3.727.128 5.478.37m-12 .92L6.16 19.673A2.25 2.25 0 008.403 21.75h7.514a2.25 2.25 0 002.244-2.077L19.228 5.79m-12-.92V3.75A2.25 2.25 0 019.478 1.5h5.044A2.25 2.25 0 0116.772 3.75v1.12" />
                              </svg>
                            }
                          />
                        </>
                      ) : (
                        <>
                          <Link to={`/company/jobs/${job.id}`}>
                            <Button 
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              iconOnly
                              aria-label="View Applications"
                              title="View Applications"
                              icon={
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12 18 18.75 12 18.75 2.25 12 2.25 12z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" />
                                </svg>
                              }
                            />
                          </Link>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleDeleteJob(job.id)}
                            className="text-red-600 hover:bg-red-50"
                            iconOnly
                            aria-label="Delete"
                            title="Delete"
                            icon={
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.108 1.022.17m-1.022-.17L18.16 19.673A2.25 2.25 0 0115.917 21.75H8.083a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .567c.34-.062.68-.118 1.022-.17m0 0A48.11 48.11 0 0112 4.5c1.886 0 3.727.128 5.478 .37m-12 .92L6.16 19.673A2.25 2.25 0 008.403 21.75h7.514a2.25 2.25 0 002.244-2.077L19.228 5.79m-12-.92V3.75A2.25 2.25 0 019.478 1.5h5.044A2.25 2.25 0 0116.772 3.75v1.12" />
                              </svg>
                            }
                          />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && filteredJobs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-600">No jobs found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/company/jobs/create">
            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-primary-lightGreen hover:bg-primary-lightGreen/10 transition-colors">
              <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <p className="mt-2 text-sm font-medium text-gray-900">Post New Job</p>
              <p className="text-xs text-gray-500">Create a new job posting</p>
            </div>
          </Link>
          <Link to="/company/applications">
            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-green-400 hover:bg-green-50 transition-colors">
              <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-2 text-sm font-medium text-gray-900">View All Applications</p>
              <p className="text-xs text-gray-500">Manage candidate applications</p>
            </div>
          </Link>
          <Link to="/company/profile">
            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-purple-400 hover:bg-purple-50 transition-colors">
              <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <p className="mt-2 text-sm font-medium text-gray-900">Company Profile</p>
              <p className="text-xs text-gray-500">Update company information</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDeleteJob}
        onConfirm={confirmDeleteJob}
        title="Delete Job Posting"
        message={`Are you sure you want to delete "${deleteDialog.jobTitle}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={loadingState.deleteJob}
      />
    </div>
  );
};

export default JobList;
